import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { FautrasLogo } from "@/components/FautrasLogo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { KeyRound, MailCheck } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const errText = (err, fallback) => {
  const d = err?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((e) => e?.msg).filter(Boolean).join(" ") || fallback;
  return fallback;
};

function Shell({ title, subtitle, children }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-[#434343] px-6">
      <div className="w-full max-w-md">
        <div className="flex justify-center mb-8"><FautrasLogo dark /></div>
        <div className="bg-white rounded-2xl p-7 sm:p-8 shadow-xl">
          <h1 className="font-heading text-2xl font-extrabold text-[#2b2b2b]">{title}</h1>
          <p className="text-sm text-muted-foreground mt-1 mb-6">{subtitle}</p>
          {children}
        </div>
      </div>
    </div>
  );
}

function ForgotForm() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email: email.trim() });
      setSent(true);
    } catch (err) { toast.error(errText(err, "Merci de saisir une adresse e-mail valide.")); }
    finally { setBusy(false); }
  };
  if (sent) {
    return (
      <Shell title="Vérifiez vos e-mails" subtitle="">
        <p className="flex gap-3 text-sm text-[#2b2b2b] leading-relaxed" data-testid="forgot-sent">
          <MailCheck className="h-5 w-5 text-[#9E7B41] shrink-0" />
          Si un compte existe pour cette adresse, vous allez recevoir un e-mail avec un lien pour choisir un nouveau mot de passe. Ce lien est valable 1 heure.
        </p>
        <Button onClick={() => navigate("/login")} data-testid="forgot-back-login"
          className="mt-6 w-full h-12 bg-[#9E7B41] hover:bg-[#876634] rounded-xl font-semibold">Retour à la connexion</Button>
      </Shell>
    );
  }
  return (
    <Shell title="Mot de passe oublié" subtitle="Saisissez l'e-mail de votre compte : nous vous envoyons un lien pour choisir un nouveau mot de passe.">
      <form onSubmit={submit} data-testid="forgot-form">
        <Label htmlFor="forgot-email" className="text-sm font-semibold">Email</Label>
        <Input id="forgot-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
          data-testid="forgot-email" className="mt-2 h-12" placeholder="vous@exemple.fr" />
        <Button type="submit" disabled={busy} data-testid="forgot-submit"
          className="mt-6 w-full h-12 bg-[#9E7B41] hover:bg-[#876634] rounded-xl font-semibold">
          {busy ? "Envoi…" : "Recevoir le lien"}
        </Button>
        <button type="button" onClick={() => navigate("/login")} data-testid="forgot-cancel"
          className="mt-4 w-full text-sm text-muted-foreground hover:text-[#9E7B41]">← Retour à la connexion</button>
      </form>
    </Shell>
  );
}

function ResetForm({ token }) {
  const navigate = useNavigate();
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 8) { toast.error("Le mot de passe doit contenir au moins 8 caractères."); return; }
    if (pw !== pw2) { toast.error("Les deux mots de passe ne correspondent pas."); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: pw });
      toast.success("Mot de passe modifié. Vous pouvez vous connecter.");
      navigate("/login");
    } catch (err) { toast.error(errText(err, "Lien invalide ou expiré.")); }
    finally { setBusy(false); }
  };
  return (
    <Shell title="Nouveau mot de passe" subtitle="Choisissez votre nouveau mot de passe (8 caractères minimum).">
      <form onSubmit={submit} data-testid="reset-form" className="space-y-4">
        <div>
          <Label htmlFor="reset-pw" className="text-sm font-semibold">Nouveau mot de passe</Label>
          <Input id="reset-pw" type="password" required value={pw} onChange={(e) => setPw(e.target.value)}
            data-testid="reset-password" className="mt-2 h-12" />
        </div>
        <div>
          <Label htmlFor="reset-pw2" className="text-sm font-semibold">Confirmer le mot de passe</Label>
          <Input id="reset-pw2" type="password" required value={pw2} onChange={(e) => setPw2(e.target.value)}
            data-testid="reset-password-confirm" className="mt-2 h-12" />
        </div>
        <Button type="submit" disabled={busy} data-testid="reset-submit"
          className="w-full h-12 bg-[#9E7B41] hover:bg-[#876634] rounded-xl font-semibold">
          <KeyRound className="h-4 w-4 mr-2" /> {busy ? "Enregistrement…" : "Enregistrer"}
        </Button>
      </form>
    </Shell>
  );
}

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token");
  return token ? <ResetForm token={token} /> : <ForgotForm />;
}
